import React, { Fragment, useMemo, useCallback, useState } from 'react';
import { Button, Typography, Grid } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import AdminTable from '@common/components/table/AdminTable';
import ApiService from '@common/services/ApiService';
import { unitResponsesStyles } from './styles';
import VcmUnitResponseDetailCell from './VcmUnitResponseDetail';
import VcmUnitResponseStatus from './VcmUnitResponseStatus';
import VcmUnitCode from './VcmUnitCode';
import UnitResponseRejectModal from './UnitResponseRejectModal';
import ErrorToast from './ErrorToast';
import { vcmStatusOptions } from './options';
import UnitResponseUpdateModal from './UnitResponseUpdateModal';

const fields = ['unit.code', 'detail', 'status'];

const api = new ApiService();

const VcmUnitResponses = ({
  vcm,
  refreshTick,
  onRefresh,
  filters,
  params,
  classes,
  institute,
  selectedCodes,
  onSelectionChange,
  onDataLoaded,
  onBatchApprove,
  onBatchReject,
  isPending
}) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [unitResponseForModal, setUnitResponseForModal] = useState(undefined);
  const [isRejectModalOpened, setIsRejectModalOpened] = useState(false);
  const [isUpdateModalOpened, setIsUpdateModalOpened] = useState(false);
  const [allUnitResponses, setAllUnitResponses] = useState([]);

  const selectedIds = useMemo(() => {
    if (!selectedCodes || selectedCodes.length === 0) return [];

    return allUnitResponses
      .filter((r) => selectedCodes.includes(r.unit.code))
      .map((r) => r.id);
  }, [selectedCodes, allUnitResponses]);

  const openRejectModal = useCallback(
    (unitResponse) => {
      setUnitResponseForModal(unitResponse);
      setIsRejectModalOpened(true);
    },
    [setUnitResponseForModal, setIsRejectModalOpened]
  );

  const openUpdateModal = useCallback(
    (unitResponse) => {
      setUnitResponseForModal(unitResponse);
      setIsUpdateModalOpened(true);
    },
    [setUnitResponseForModal, setIsUpdateModalOpened]
  );

  const handleCloseRejectModal = useCallback(() => {
    setUnitResponseForModal(undefined);
    setIsRejectModalOpened(false);
  }, [setIsRejectModalOpened, setUnitResponseForModal]);

  const handleCloseUpdateModal = useCallback(() => {
    setUnitResponseForModal(undefined);
    setIsUpdateModalOpened(false);
  }, [setIsUpdateModalOpened, setUnitResponseForModal]);

  const metadata = useMemo(
    () => ({
      'unit.code': {
        type: 'component',
        label: 'Unit',
        component: VcmUnitCode,
        tableCellProps: { className: classes.tableCell }
      },
      detail: {
        type: 'component',
        label: 'Details',
        component: VcmUnitResponseDetailCell,
        componentProps: { vcm, onEdit: openUpdateModal, institute },
        tableCellProps: { className: classes.tableCell }
      },
      status: {
        type: 'component',
        label: 'Status',
        component: VcmUnitResponseStatus,
        tableCellProps: { className: classes.tableCell }
      }
    }),
    [classes.tableCell, vcm, institute, openUpdateModal]
  );

  const handleApprove = useCallback(async (unitResponse) => {
    try {
      const url = `/api/v1/vcms/${unitResponse.vcm_id}/unit_responses/${unitResponse.unit.id}/approve`;
      const res = await api.put(url);
      if (res.status >= 200 && res.status < 400) {
        onRefresh();
      } else {
        setErrorMessage(res.json.message);
      }
    } catch (error) {
      setErrorMessage('Something went wrong, please try again');
    }
  }, [onRefresh]);

  const ApproveButton = ({ unitResponse }) => {
    return (
      <Button
        disabled={isPending || vcm.status !== vcmStatusOptions.in_review.id}
        onClick={() => {
          handleApprove(unitResponse);
        }}
        className={`${classes.actionButtons} ${classes.approveButton}`}
        color="secondary"
        size="small"
        variant="contained"
      >
        <Typography variant="button">Approve</Typography>
      </Button>
    );
  };

  const RejectButton = ({ unitResponse }) => {
    return (
      <Button
        disabled={isPending || vcm.status !== vcmStatusOptions.in_review.id}
        onClick={() => {
          openRejectModal(unitResponse);
        }}
        className={`${classes.actionButtons} ${classes.rejectButton}`}
        color="secondary"
        size="small"
        variant="contained"
      >
        <Typography variant="button">Mark For Attention</Typography>
      </Button>
    );
  };

  const renderButtonActions = useCallback((unitResponse) => {
    if (!unitResponse || unitResponse.removed) {
      return null;
    }

    return (
      <>
        <ApproveButton unitResponse={unitResponse} />
        <RejectButton unitResponse={unitResponse} />
      </>
    );
  }, []);

  const handleDataLoaded = useCallback((data) => {
    setAllUnitResponses(data.data || []);
    if (onDataLoaded) {
      onDataLoaded(data);
    }
  }, [onDataLoaded]);

  const handleSelectionChange = useCallback((newSelectedIds) => {
    if (onSelectionChange) {
      const newSelectedCodes = allUnitResponses
        .filter((r) => newSelectedIds.includes(r.id))
        .map((r) => r.unit.code);

      onSelectionChange(newSelectedCodes);
    }
  }, [onSelectionChange, allUnitResponses]);

  return (
    <>
      <AdminTable
        tableBottomPadding
        actionsSection={['buttons']}
        renderButtonActions={renderButtonActions}
        fields={fields}
        filters={filters}
        params={params}
        metadata={metadata}
        resource={`vcms/${vcm.id}/unit_responses`}
        actionCellProps={{
          className: classes.tableCell,
          width: 300
        }}
        refreshTick={refreshTick}
        enableRowSelection={vcm && vcm.status === vcmStatusOptions.in_review.id && !isPending}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        onDataLoaded={handleDataLoaded}
      />
      <UnitResponseRejectModal
        isOpened={isRejectModalOpened}
        onClose={handleCloseRejectModal}
        unitResponses={unitResponseForModal ? [unitResponseForModal] : []}
        onRefresh={onRefresh}
      />
      <UnitResponseUpdateModal
        vcm={vcm}
        isOpened={isUpdateModalOpened}
        onClose={handleCloseUpdateModal}
        unitResponse={unitResponseForModal}
        onRefresh={onRefresh}
      />
      <ErrorToast
        open={!!errorMessage}
        errorMessage={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
};

export default withStyles(VcmUnitResponses, unitResponsesStyles, {
  withTheme: true
});
